import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { url: String }
  static targets = ["submitButton"]

  connect() {
    this.saveTimeout = null
    this.originalFormData = this.captureFormData()
    this.updateButtonState()
  }

  disconnect() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
    }
  }

  scheduleUpdate() {
    this.updateButtonState()
  }

  captureFormData() {
    const form = this.element
    if (!(form instanceof HTMLFormElement)) return null

    const formData = new FormData(form)
    const data = {}

    for (const [key, value] of formData.entries()) {
      data[key] = value
    }

    return JSON.stringify(data)
  }

  hasChanges() {
    return this.captureFormData() !== this.originalFormData
  }

  updateButtonState() {
    if (!this.hasSubmitButtonTarget) return
    this.submitButtonTarget.disabled = !this.hasChanges()
  }

  async save(event) {
    // Create FormData from the form
    const form = this.element
    if (!(form instanceof HTMLFormElement)) {
      console.error("auto-save controller must be attached to a form element")
      return
    }

    // Get the submit button
    const submitButton = this.hasSubmitButtonTarget ? this.submitButtonTarget : event?.target?.closest('button[type="submit"], button:not([type])')

    if (!submitButton) {
      console.error("No submit button found")
      return
    }

    // Disable button during save
    submitButton.disabled = true

    // Copy date values from DatePicker portal inputs to form before collecting FormData
    this.copyDatePickerValues(form)

    const formData = new FormData(form)
    const data = {}

    // Convert FormData to nested object structure (supports deep keys and arrays)
    for (const [key, value] of formData.entries()) {
      const segments = this.parseFormKey(key)
      const isArrayField = segments[segments.length - 1] === ""

      if (!isArrayField && (value === null || value === undefined || value === "")) {
        continue
      }

      this.assignFormValue(data, segments, value)
    }

    try {
      const response = await fetch(this.urlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
          'Accept': 'text/vnd.turbo-stream.html'
        },
        body: JSON.stringify(data)
      })

      if (response.ok) {
        // Get the Turbo Stream response and let Turbo handle it
        const turboStreamHtml = await response.text()

        // Use Turbo to apply the stream actions
        if (turboStreamHtml && window.Turbo) {
          window.Turbo.renderStreamMessage(turboStreamHtml)
        }

        // Show success toast
        if (window.toastController) {
          window.toastController.success("Inloop updated")
        }

        // Capture new form state and disable button
        this.originalFormData = this.captureFormData()
        this.updateButtonState()
      } else {
        const error = await response.json()
        console.error('Save error:', error)
        if (window.toastController) {
          window.toastController.error("Failed to save changes")
        }

        // Re-enable button (changes still pending)
        this.updateButtonState()
      }
    } catch (error) {
      console.error('Save error:', error)
      if (window.toastController) {
        window.toastController.error("Failed to save changes")
      }

      // Re-enable button (changes still pending)
      this.updateButtonState()
    }
  }

  copyDatePickerValues(form) {
    const datePickerInputs = document.querySelectorAll('[data-date-picker-target="input"], [data-date-picker-target="startInput"], [data-date-picker-target="endInput"]')

    datePickerInputs.forEach(input => {
      if (input.value && input.name) {
        const existingHiddenInput = form.querySelector(`input[type="hidden"][name="${input.name}"]`)

        if (existingHiddenInput) {
          existingHiddenInput.value = input.value
        } else {
          const hiddenInput = document.createElement('input')
          hiddenInput.type = 'hidden'
          hiddenInput.name = input.name
          hiddenInput.value = input.value
          form.appendChild(hiddenInput)
        }
      }
    })
  }

  parseFormKey(key) {
    return key.replace(/\]/g, "").split("[")
  }

  assignFormValue(target, segments, value) {
    let current = target

    for (let i = 0; i < segments.length; i += 1) {
      const segment = segments[i]
      const isLast = i === segments.length - 1
      const nextIsArray = segments[i + 1] === ""

      if (segment === "") {
        if (Array.isArray(current)) {
          current.push(value)
        }
        return
      }

      if (isLast) {
        current[segment] = value
        return
      }

      if (current[segment] === undefined) {
        current[segment] = nextIsArray ? [] : {}
      }

      current = current[segment]
    }
  }
}
